import { DeepResearchRun } from "@/pages/Private/redux/deepResearch/schema";

/**
 * The page's picture of each prospect's deep research (PREC-4052), seeded from the server's run
 * rows and kept current by the socket. Pure, so the controller only holds the maps.
 */
export type DeepResearchPhase = "queued" | "running";

export interface DeepResearchRunView {
	phase: DeepResearchPhase;
	/** When the phase began, epoch ms: requested (queued) or handed to a worker (running). */
	since: number;
}

export interface PendingRun extends DeepResearchRunView {
	/**
	 * The research the lead showed when this page started the run (`null` = none yet). A lead that
	 * later carries a different one has finished the run even if its socket event was lost
	 * (PREC-4024). Unset on a run the server reported: the server's next answer ends it.
	 */
	baselineResponseId?: number | null;
	/** Started on this page; kept against a server answer that was asked before it existed. */
	local?: boolean;
}

/** A refusal to spend cannot succeed again until credits are added; anything else can be retried. */
export type DeepResearchFailureKind = "credits" | "failed";

export interface DeepResearchFailure {
	kind: DeepResearchFailureKind;
	/** When this page learned of it (epoch ms), to tell it from a server answer asked earlier. */
	learnedAt: number;
	/** When the run failed, by the server's clock; unset for a failure the socket reported. */
	failedAt?: number;
}

export interface RunSeed {
	runs: DeepResearchRun[];
	asked: number[];
	/** When the question was sent (epoch ms). */
	askedAt: number;
	/**
	 * Failures the user closed, by prospect: the closed failure's `failedAt`, or the time it was
	 * closed for one the socket reported. A failure at or before it stays closed.
	 */
	dismissedAt: Map<number, number>;
}

const timeOf = (iso: string | null | undefined): number => {
	const parsed = iso ? Date.parse(iso) : NaN;

	return Number.isNaN(parsed) ? 0 : parsed;
};

/** When the run's current phase began. */
export const phaseSinceOf = (run: DeepResearchRun): number =>
	run.state === "running" ? timeOf(run.publishedAt ?? run.createdAt) : timeOf(run.createdAt);

export const failureKindOfReason = (
	reason: DeepResearchRun["failureReason"]
): DeepResearchFailureKind => (reason === "insufficient_credits" ? "credits" : "failed");

const runsByProspect = (runs: DeepResearchRun[]) =>
	new Map(runs.map((run) => [run.prospectId, run]));

/**
 * The pending runs after a server answer: the server's queued and running runs replace the page's
 * own, and a run the server no longer reports ends, unless this page started it after the question
 * was sent.
 */
export function seedPendingRuns(
	prev: Map<number, PendingRun>,
	seed: RunSeed
): Map<number, PendingRun> {
	const next = new Map(prev);
	const byProspect = runsByProspect(seed.runs);

	for (const prospectId of seed.asked) {
		const run = byProspect.get(prospectId);
		const local = prev.get(prospectId);

		if (run && run.state !== "failed") {
			next.set(prospectId, {
				phase: run.state,
				since: phaseSinceOf(run),
				baselineResponseId: local?.baselineResponseId,
			});
		} else if (!local?.local || local.since <= seed.askedAt) {
			next.delete(prospectId);
		}
	}

	return next;
}

/**
 * The failures after a server answer: the latest run's failure is shown unless the user closed it
 * or this page has started a run since; a queued, running or finished run clears the failure,
 * unless the socket reported it after the question was sent.
 */
export function seedFailures(
	prev: Map<number, DeepResearchFailure>,
	pendingAfter: Map<number, PendingRun>,
	seed: RunSeed
): Map<number, DeepResearchFailure> {
	const next = new Map(prev);
	const byProspect = runsByProspect(seed.runs);

	for (const prospectId of seed.asked) {
		const run = byProspect.get(prospectId);

		if (run?.state === "failed" && !pendingAfter.has(prospectId)) {
			const dismissedAt = seed.dismissedAt.get(prospectId);

			if (dismissedAt !== undefined && timeOf(run.failedAt) <= dismissedAt) {
				next.delete(prospectId);
			} else {
				next.set(prospectId, {
					kind: failureKindOfReason(run.failureReason),
					learnedAt: seed.askedAt,
					failedAt: timeOf(run.failedAt),
				});
			}

			continue;
		}

		const failure = prev.get(prospectId);

		if (failure && failure.learnedAt <= seed.askedAt) {
			next.delete(prospectId);
		}
	}

	return next;
}

/** A run is over once the lead shows a research other than the one it started from. */
export const isSupersededBy = (run: PendingRun, responseId: number): boolean =>
	run.baselineResponseId !== undefined && run.baselineResponseId !== responseId;
